import { Component, DestroyRef, ElementRef, OnInit, Signal, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { PartnerModel, PartnerModelApiService, SearchPartnerModelsRequest } from '@vendasta/partner';
import { fromEvent, take } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, scan, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/auth.service';
import { PartnerSwitcherService } from './partner-switcher.service';

const PAGE_SIZE = 15;

@Component({
  templateUrl: './partner-switcher.component.html',
  styleUrls: ['../common.scss'],
})
export class PartnerSwitcherComponent implements OnInit {
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

  private _searchTerm = signal('');
  private _nextCursor = signal('');
  private _loading = signal(false);

  state: Signal<State>;
  recentPartners: Signal<PartnerModel[]>;
  loading = this._loading.asReadonly();
  protected invalidPartnerId = signal(false);

  constructor(
    private authService: AuthService,
    private partnerModelService: PartnerModelApiService,
    private partnerSwitcherService: PartnerSwitcherService,
    public dialogRef: MatDialogRef<PartnerSwitcherComponent>,
    private destroyRef: DestroyRef,
  ) {
    const searchTerm$ = toObservable(this._searchTerm).pipe(distinctUntilChanged());
    const nextCursor$ = toObservable(this._nextCursor).pipe(distinctUntilChanged());
    const state$ = searchTerm$.pipe(
      switchMap((searchTerm) => {
        return nextCursor$.pipe(
          tap(() => this._loading.set(true)),
          switchMap((nextCursor) => {
            return this.partnerModelService
              .searchPartnerModels(
                new SearchPartnerModelsRequest({
                  cursor: nextCursor,
                  pageSize: PAGE_SIZE,
                  filters: {
                    searchTerm: searchTerm,
                  },
                }),
              )
              .pipe(
                map((response) => {
                  return {
                    partners: response?.partners || [],
                    searchTerm: searchTerm,
                    cursor: response?.nextCursor || '',
                    hasMore: response?.hasMore || false,
                  };
                }),
                shareReplay(1),
              );
          }),
          scan((state, newState) => {
            return {
              ...newState,
              partners: state.partners.concat(newState.partners),
            };
          }, defaultState),
          tap(() => this._loading.set(false)),
        );
      }),
    );
    this.state = toSignal(state$);
    this.recentPartners = this.partnerSwitcherService.recentPartners;
  }

  ngOnInit(): void {
    fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        distinctUntilChanged(),
        debounceTime(300),
        map(() => this.searchInput.nativeElement.value as string),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((searchTerm) => {
        this._searchTerm.set(searchTerm);
        this._nextCursor.set('');
      });
  }

  loadMore(cursor: string): void {
    this._nextCursor.set(cursor);
  }

  close(): void {
    this.dialogRef.close();
  }

  onSuccess(): void {
    this.close();
    const isWorkOrdersPath = window.location.pathname.includes('/work-orders');
    const isProductsPath = window.location.pathname.includes('/products');
    if (isWorkOrdersPath || isProductsPath) {
      const newPath = isWorkOrdersPath ? '/work-orders' : '/products';
      window.location.assign(newPath);
    } else {
      window.location.reload();
    }
  }

  onError(): void {
    this.invalidPartnerId.set(true);
  }

  switchPartner(partnerId: string): void {
    if (!partnerId) return;
    const partnerIdUpperCase = partnerId.toUpperCase();
    this.authService
      .setPartnerId(partnerIdUpperCase)
      .pipe(take(1))
      .subscribe((result) => {
        if (result === 'success') {
          this.onSuccess();
        } else if (result === 'invalidPartner') {
          this.onError();
        } else if (result === 'alreadySelected') {
          this.close();
        }
      });
  }
}

interface State {
  partners: PartnerModel[];
  searchTerm: string;
  cursor: string;
  hasMore: boolean;
}

const defaultState = {
  partners: [],
  searchTerm: '',
  nextCursor: '',
  hasMore: false,
};
